import { useKeyboardInset } from "@/hooks/useKeyboardInset";
import { ChatRequestMode } from "@shaarp/common";
import { useRef, useState } from "react";
import { MemoryMessageToSend } from "@/components/chat/chat-conversation";
import { ChatComposerDisclaimer } from "@/components/chat/chat-dialog-primitives";
import { ChatNewGreeting } from "@/components/chat/chat-new-greeting";
import { ChatRecentCompanies } from "@/components/chat/chat-recent-companies";
import { ChatRequestModePills } from "@/components/chat/chat-request-mode-pills";
import { ChatTextInput, ChatTextInputRef } from "@/components/chat/chat-text-input";

/**
 * The desktop welcome screen, laid out for a phone as in the Claude app: the
 * greeting in the middle of the screen; under it the companies last opened, a
 * row the thumb swipes; and at the bottom, under
 * the thumb, the request modes and the composer — where the conversation's
 * composer will be once the message is sent, so nothing has to travel.
 */
export function ChatNewMobileHome({
  onMessageSent,
  streamInProgress = false,
  draftToRestore,
  onDraftRestored,
}: {
  readonly onMessageSent: (params: MemoryMessageToSend) => void;
  readonly streamInProgress?: boolean;
  /** Text the composer takes back: a creation that did not go through. */
  readonly draftToRestore?: { text: string; attempt: number } | null;
  /** The composer took the draft back. */
  readonly onDraftRestored?: () => void;
}) {
  const chatInputRef = useRef<ChatTextInputRef>(null);
  // iOS only shrinks the visual viewport for the keyboard: lift the composer
  // by the part of the keyboard over the home indicator, as the conversation
  // does.
  const keyboardInset = useKeyboardInset();
  // Mirrors the composer's armed mode, as on the desktop welcome screen.
  const [requestMode, setRequestMode] = useState<ChatRequestMode | null>(null);

  // Unlike the desktop, a mode does not focus the composer: on a phone that
  // opens the keyboard and folds the recent companies away, just when the
  // user may want to pick one for the mode. The keyboard comes with a tap on
  // the field.
  const handleModeToggle = (mode: ChatRequestMode) => {
    chatInputRef.current?.setRequestMode(requestMode === mode ? null : mode);
  };

  return (
    <div className="group/home flex flex-col h-full">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
        <div className="flex flex-1 justify-center items-center px-6 py-4 text-center">
          <ChatNewGreeting />
        </div>
        {/* While the composer has the focus the user is typing, not picking a
            shortcut: the row steps aside so the greeting keeps the little room
            the keyboard leaves. Read from the focus, not the keyboard inset,
            which Android never reports. Collapsed rather than `hidden` or
            unmounted: `display: none` would replay its entrance on the way
            back, and a remount would ask for it again. */}
        <div className="pb-3 overflow-hidden group-has-[textarea:focus]/home:invisible group-has-[textarea:focus]/home:h-0 group-has-[textarea:focus]/home:pb-0">
          <ChatRecentCompanies layout="row" />
        </div>
      </div>
      <div
        className="flex flex-col gap-3 px-3 pb-2 shrink-0"
        style={
          keyboardInset > 0
            ? {
                marginBottom: `max(0px, calc(${keyboardInset}px - var(--mobile-bottom-inset)))`,
              }
            : undefined
        }
      >
        <ChatRequestModePills
          activeMode={requestMode}
          onToggle={handleModeToggle}
          disabled={streamInProgress}
        />
        <ChatTextInput
          ref={chatInputRef}
          onMessageSent={onMessageSent}
          streamInProgress={streamInProgress}
          isCompanyIdentifierShortcutEnabled
          onRequestModeChange={setRequestMode}
          draftToRestore={draftToRestore}
          onDraftRestored={onDraftRestored}
        />
        {/* Said once, where a conversation starts: the floating composer of
            a conversation carries no tray for it. */}
        <ChatComposerDisclaimer className="mt-0 pt-0 pb-0 bg-transparent" />
      </div>
    </div>
  );
}
